import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useSettingsStore = defineStore('settings', () => {
    // Store Profile Settings
    const storeName = ref('')
    const storeUrl = ref('')
    const storeDescription = ref('')
    const storeLogo = ref('')
    const storeFavicon = ref('')
    const storeAddress = ref('')
    const storeTaxId = ref('')
    const storePhone = ref('')
    
    // Warehouse Settings
    const warehouseLat = ref('')
    const warehouseLng = ref('')

    const isOnlineShoppingEnabled = ref(true)
    const isWishlistEnabled = ref(true)
    const isCompareEnabled = ref(true)
    const isAiRecommendationEnabled = ref(true)
    const showProductRating = ref(false)
    const showProductReview = ref(false)
    
    // Maintenance & Holiday Modes
    const maintenanceModeEnabled = ref(false)
    const maintenanceMessage = ref('ขออภัยค่ะ ขณะนี้เว็บไซต์อยู่ระหว่างการปรับปรุงระบบชั่วคราว กรุณาติดต่อทางไลน์หรือโทรศัพท์')
    const holidayModeEnabled = ref(false)
    const holidayMessage = ref('ร้านค้าอยู่ในช่วงวันหยุดยาว การจัดส่งอาจมีระยะเวลานานกว่าปกติ ขออภัยในความไม่สะดวกค่ะ')
    const holidayName = ref('')
    const holidayStartDate = ref('')  // ISO date string YYYY-MM-DD
    const holidayEndDate = ref('')    // ISO date string YYYY-MM-DD
    const holidayImage = ref('')      // URL of banner image

    // Auto-activate holiday mode if today is within the scheduled range
    const isHolidayActive = computed(() => {
        if (!holidayModeEnabled.value) return false
        // If no schedule set, just use the enabled toggle
        if (!holidayStartDate.value || !holidayEndDate.value) return true
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const start = new Date(holidayStartDate.value)
        start.setHours(0, 0, 0, 0)
        const end = new Date(holidayEndDate.value)
        end.setHours(23, 59, 59, 999)
        return today >= start && today <= end
    })

    const paymentPromptpayEnabled = ref(false)
    const paymentBankTransferEnabled = ref(false)
    const paymentIpayEnabled = ref(false)

    // Bank details (Array of objects)
    const paymentBankAccounts = ref([])

    // PromptPay details
    const paymentPromptpayNumber = ref('')

    // Shipping Restrictions (Array of strings)
    const shippingRestrictedProvinces = ref([])

    // Free Installation Provinces (Array of province names)
    const freeInstallProvinces = ref(['กรุงเทพมหานคร', 'นนทบุรี', 'ปทุมธานี', 'สมุทรปราการ', 'สมุทรสาคร', 'นครปฐม'])

    // Contact Channels
    const contactPhones = ref([])
    const contactEmails = ref([])
    const contactLines = ref([])
    const contactFacebookUrl = ref('')
    const contactTiktokUrl = ref('')
    const contactYoutubeUrl = ref('')

    // Footer Newsletter Banner Settings
    const footerBannerBadge = ref('โรงเรียนอัจฉริยะ มาตรฐานระดับสากล')
    const footerBannerTitle = ref('เริ่มต้นสร้าง <span class="text-emerald-800 dark:text-emerald-400">ผลผลิตที่ดีกว่า</span>')
    const footerBannerSubtitle = ref('ติดตามข่าวสาร นวัตกรรมโรงเรียนอัจฉริยะ และโปรโมชั่นสุดพิเศษก่อนใคร สมัครเลยวันนี้!')
    const footerBannerPlaceholder = ref('กรอกอีเมลของคุณที่นี่...')
    const footerBannerButton = ref('ติดตามข่าวสาร')
    const footerBannerSuccess = ref('ขอบคุณที่ติดตามข่าวสาร!')
    const footerBannerImage = ref('/images/home/cta_greenhouse.png')

    // Dynamic Footer settings
    const footerMainMenu = ref([])
    const footerHelpMenu = ref([])
    const footerCopyrightName = ref('GreenHouse Smart Farming Solutions')

    // Products Header settings
    const productsHeaderBadge = ref('พร้อมจัดส่งทั่วประเทศ')
    const productsHeaderTitleMain = ref('แคตตาล็อกสินค้ากว่า')
    const productsHeaderTitleNumber = ref('100')
    const productsHeaderTitleSuffix = ref('แบบ')
    const productsHeaderDescription = ref('ค้นพบพื้นที่เก็บของ โกดัง และโรงเรือนทุกขนาด ที่ออกแบบมาเพื่อยกระดับพื้นที่ของคุณ\nโดยเฉพาะ สวยงาม ทนแดด ทนฝน ด้วยวัสดุคุณภาพในดีไซน์ที่ลงตัว')
    const productsHeaderFeatures = ref([
      { title: 'วัสดุคุณภาพสูง', subtitle: 'ทนทานทุกสภาพอากาศ', icon: 'shield' },
      { title: 'ออกแบบสวยงาม', subtitle: 'ลงตัวกับทุกพื้นที่', icon: 'compass' },
      { title: 'ติดตั้งง่าย', subtitle: 'รวดเร็ว ได้มาตรฐาน', icon: 'tools' }
    ])

    const initializeSettings = (pubSettings) => {
        if (!pubSettings) return

        if (pubSettings.online_shopping_enabled !== undefined) {
            isOnlineShoppingEnabled.value = String(pubSettings.online_shopping_enabled) === 'true'
        }
        if (pubSettings.wishlist_enabled !== undefined) {
            isWishlistEnabled.value = String(pubSettings.wishlist_enabled) === 'true'
        }
        if (pubSettings.compare_enabled !== undefined) {
            isCompareEnabled.value = String(pubSettings.compare_enabled) === 'true'
        }
        if (pubSettings.ai_recommendation_enabled !== undefined) {
            isAiRecommendationEnabled.value = String(pubSettings.ai_recommendation_enabled) === 'true'
        }

        if (pubSettings.show_product_rating !== undefined) {
            showProductRating.value = String(pubSettings.show_product_rating) === 'true'
        }
        if (pubSettings.show_product_review !== undefined) {
            showProductReview.value = String(pubSettings.show_product_review) === 'true'
        }

        if (pubSettings.maintenance_mode_enabled !== undefined) {
            maintenanceModeEnabled.value = String(pubSettings.maintenance_mode_enabled) === 'true'
        }
        if (pubSettings.maintenance_message !== undefined) {
            maintenanceMessage.value = pubSettings.maintenance_message
        }
        if (pubSettings.holiday_mode_enabled !== undefined) {
            holidayModeEnabled.value = String(pubSettings.holiday_mode_enabled) === 'true'
        }
        if (pubSettings.holiday_message !== undefined) {
            holidayMessage.value = pubSettings.holiday_message
        }
        if (pubSettings.holiday_name !== undefined) {
            holidayName.value = pubSettings.holiday_name
        }
        if (pubSettings.holiday_start_date !== undefined) {
            holidayStartDate.value = pubSettings.holiday_start_date
        }
        if (pubSettings.holiday_end_date !== undefined) {
            holidayEndDate.value = pubSettings.holiday_end_date
        }
        if (pubSettings.holiday_image !== undefined) {
            holidayImage.value = pubSettings.holiday_image
        }

        // Store Settings
        if (pubSettings.store_name !== undefined) storeName.value = pubSettings.store_name || '';
        if (pubSettings.store_url !== undefined) storeUrl.value = pubSettings.store_url || '';
        if (pubSettings.store_description !== undefined) storeDescription.value = pubSettings.store_description || '';
        if (pubSettings.store_logo !== undefined) storeLogo.value = pubSettings.store_logo || '';
        if (pubSettings.store_favicon !== undefined) storeFavicon.value = pubSettings.store_favicon || '';
        if (pubSettings.store_address !== undefined) storeAddress.value = pubSettings.store_address || '';
        if (pubSettings.store_tax_id !== undefined) storeTaxId.value = pubSettings.store_tax_id || '';
        if (pubSettings.store_phone !== undefined) storePhone.value = pubSettings.store_phone || '';
        
        if (pubSettings.warehouse_lat !== undefined) warehouseLat.value = pubSettings.warehouse_lat;
        if (pubSettings.warehouse_lng !== undefined) warehouseLng.value = pubSettings.warehouse_lng;

        // Contact Channels
        if (pubSettings.contact_phones) {
            try { const p = typeof pubSettings.contact_phones === 'string' ? JSON.parse(pubSettings.contact_phones) : pubSettings.contact_phones; if (p.length) contactPhones.value = p } catch (e) {}
        }
        if (pubSettings.contact_emails) {
            try { const e = typeof pubSettings.contact_emails === 'string' ? JSON.parse(pubSettings.contact_emails) : pubSettings.contact_emails; if (e.length) contactEmails.value = e } catch (e) {}
        }
        if (pubSettings.contact_lines) {
            try { const l = typeof pubSettings.contact_lines === 'string' ? JSON.parse(pubSettings.contact_lines) : pubSettings.contact_lines; if (l.length) contactLines.value = l } catch (e) {}
        }
        if (pubSettings.contact_facebook_url !== undefined) contactFacebookUrl.value = pubSettings.contact_facebook_url;
        if (pubSettings.contact_tiktok_url !== undefined) contactTiktokUrl.value = pubSettings.contact_tiktok_url;
        if (pubSettings.contact_youtube_url !== undefined) contactYoutubeUrl.value = pubSettings.contact_youtube_url;

        // Footer Newsletter Banner Settings
        if (pubSettings.footer_banner_badge !== undefined) footerBannerBadge.value = pubSettings.footer_banner_badge;
        if (pubSettings.footer_banner_title !== undefined) footerBannerTitle.value = pubSettings.footer_banner_title;
        if (pubSettings.footer_banner_subtitle !== undefined) footerBannerSubtitle.value = pubSettings.footer_banner_subtitle;
        if (pubSettings.footer_banner_placeholder !== undefined) footerBannerPlaceholder.value = pubSettings.footer_banner_placeholder;
        if (pubSettings.footer_banner_button !== undefined) footerBannerButton.value = pubSettings.footer_banner_button;
        if (pubSettings.footer_banner_success !== undefined) footerBannerSuccess.value = pubSettings.footer_banner_success;
        if (pubSettings.footer_banner_image !== undefined) footerBannerImage.value = pubSettings.footer_banner_image;

        // Dynamic Footer settings
        if (pubSettings.footer_main_menu) {
            try {
                footerMainMenu.value = typeof pubSettings.footer_main_menu === 'string' 
                    ? JSON.parse(pubSettings.footer_main_menu) 
                    : pubSettings.footer_main_menu;
            } catch (e) {
                console.error('Failed to parse footer_main_menu:', e);
                footerMainMenu.value = [];
            }
        }
        if (pubSettings.footer_help_menu) {
            try {
                footerHelpMenu.value = typeof pubSettings.footer_help_menu === 'string' 
                    ? JSON.parse(pubSettings.footer_help_menu) 
                    : pubSettings.footer_help_menu;
            } catch (e) {
                console.error('Failed to parse footer_help_menu:', e);
                footerHelpMenu.value = [];
            }
        }
        if (pubSettings.footer_copyright_name !== undefined) {
            footerCopyrightName.value = pubSettings.footer_copyright_name;
        }

        // Apply dynamically to DOM
        if (storeName.value) {
            document.title = storeName.value;
        }

        const updateMetaTag = (selector, attr, val) => {
            if (!val) return;
            let tag = document.querySelector(selector);
            if (!tag) {
                tag = document.createElement('meta');
                if (selector.includes('name=')) {
                    const m = selector.match(/name="([^"]+)"/);
                    if (m) tag.setAttribute('name', m[1]);
                } else if (selector.includes('property=')) {
                    const m = selector.match(/property="([^"]+)"/);
                    if (m) tag.setAttribute('property', m[1]);
                }
                document.head.appendChild(tag);
            }
            tag.setAttribute(attr, val);
        };

        if (storeName.value) {
            updateMetaTag('meta[name="author"]', 'content', storeName.value);
            updateMetaTag('meta[property="og:site_name"]', 'content', storeName.value);
            updateMetaTag('meta[property="og:title"]', 'content', storeName.value);
            updateMetaTag('meta[name="twitter:title"]', 'content', storeName.value);
        }
        if (storeDescription.value) {
            updateMetaTag('meta[name="description"]', 'content', storeDescription.value);
            updateMetaTag('meta[property="og:description"]', 'content', storeDescription.value);
            updateMetaTag('meta[name="twitter:description"]', 'content', storeDescription.value);
        }
        if (storeUrl.value) {
            updateMetaTag('meta[property="og:url"]', 'content', storeUrl.value);
        }
        if (storeLogo.value || storeFavicon.value) {
            const img = storeLogo.value || storeFavicon.value;
            updateMetaTag('meta[property="og:image"]', 'content', img);
            updateMetaTag('meta[name="twitter:image"]', 'content', img);
        }

        if (storeFavicon.value) {
            const existingIcons = document.querySelectorAll("link[rel*='icon']");
            existingIcons.forEach(el => el.remove());
            ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
                const link = document.createElement('link');
                link.rel = rel;
                link.href = storeFavicon.value;
                document.head.appendChild(link);
            });

            try {
                const manifestData = {
                    name: storeName.value || 'Store',
                    short_name: storeName.value || 'Store',
                    description: storeDescription.value || '',
                    start_url: '/',
                    display: 'standalone',
                    background_color: '#ffffff',
                    theme_color: '#10b981',
                    icons: [
                        { src: storeFavicon.value, sizes: '192x192', type: 'image/webp', purpose: 'any maskable' },
                        { src: storeFavicon.value, sizes: '512x512', type: 'image/webp', purpose: 'any maskable' }
                    ]
                };
                const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' });
                const manifestUrl = URL.createObjectURL(blob);
                let manifestLink = document.querySelector('link[rel="manifest"]');
                if (manifestLink) {
                    manifestLink.href = manifestUrl;
                } else {
                    manifestLink = document.createElement('link');
                    manifestLink.rel = 'manifest';
                    manifestLink.href = manifestUrl;
                    document.head.appendChild(manifestLink);
                }
            } catch (e) { }
        }

        if (pubSettings.payment_promptpay_enabled !== undefined) {
            paymentPromptpayEnabled.value = String(pubSettings.payment_promptpay_enabled) === 'true'
        }
        if (pubSettings.payment_bank_transfer_enabled !== undefined) {
            paymentBankTransferEnabled.value = String(pubSettings.payment_bank_transfer_enabled) === 'true'
        }
        if (pubSettings.payment_ipay_enabled !== undefined) {
            paymentIpayEnabled.value = String(pubSettings.payment_ipay_enabled) === 'true'
        }

        if (pubSettings.payment_bank_accounts) {
            try {
                paymentBankAccounts.value = typeof pubSettings.payment_bank_accounts === 'string'
                    ? JSON.parse(pubSettings.payment_bank_accounts)
                    : pubSettings.payment_bank_accounts;
            } catch (e) {
                console.error('Failed to parse payment_bank_accounts:', e);
                paymentBankAccounts.value = [];
            }
        }

        if (pubSettings.payment_promptpay_number !== undefined) {
            paymentPromptpayNumber.value = pubSettings.payment_promptpay_number
        }

        if (pubSettings.shipping_restricted_provinces) {
            try {
                shippingRestrictedProvinces.value = typeof pubSettings.shipping_restricted_provinces === 'string'
                    ? JSON.parse(pubSettings.shipping_restricted_provinces)
                    : pubSettings.shipping_restricted_provinces;
                if (!Array.isArray(shippingRestrictedProvinces.value)) {
                    shippingRestrictedProvinces.value = [];
                }
            } catch (e) {
                console.error('Failed to parse shipping_restricted_provinces:', e);
                shippingRestrictedProvinces.value = [];
            }
        }

        if (pubSettings.free_install_provinces) {
            try {
                const parsed = typeof pubSettings.free_install_provinces === 'string'
                    ? JSON.parse(pubSettings.free_install_provinces)
                    : pubSettings.free_install_provinces;
                if (Array.isArray(parsed) && parsed.length > 0) {
                    freeInstallProvinces.value = parsed;
                }
            } catch (e) {
                console.error('Failed to parse free_install_provinces:', e);
            }
        }

        if (pubSettings.products_header_badge !== undefined) {
            productsHeaderBadge.value = pubSettings.products_header_badge
        }
        if (pubSettings.products_header_title_main !== undefined) {
            productsHeaderTitleMain.value = pubSettings.products_header_title_main
        }
        if (pubSettings.products_header_title_number !== undefined) {
            productsHeaderTitleNumber.value = pubSettings.products_header_title_number
        }
        if (pubSettings.products_header_title_suffix !== undefined) {
            productsHeaderTitleSuffix.value = pubSettings.products_header_title_suffix
        }
        if (pubSettings.products_header_description !== undefined) {
            productsHeaderDescription.value = pubSettings.products_header_description
        }
        if (pubSettings.products_header_features !== undefined) {
            try {
                productsHeaderFeatures.value = typeof pubSettings.products_header_features === 'string'
                    ? JSON.parse(pubSettings.products_header_features)
                    : pubSettings.products_header_features
            } catch (e) {
                console.error('Failed to parse products_header_features:', e)
            }
        }
    }

    return {
        storeName,
        storeUrl,
        storeDescription,
        storeLogo,
        storeFavicon,
        storeAddress,
        storeTaxId,
        storePhone,
        warehouseLat,
        warehouseLng,
        isOnlineShoppingEnabled,
        isWishlistEnabled,
        isCompareEnabled,
        isAiRecommendationEnabled,
        paymentPromptpayEnabled,
        paymentBankTransferEnabled,
        paymentIpayEnabled,
        paymentBankAccounts,
        paymentPromptpayNumber,
        shippingRestrictedProvinces,
        freeInstallProvinces,
        showProductRating,
        showProductReview,
        maintenanceModeEnabled,
        maintenanceMessage,
        holidayModeEnabled,
        holidayMessage,
        holidayName,
        holidayStartDate,
        holidayEndDate,
        isHolidayActive,
        holidayImage,
        contactPhones,
        contactEmails,
        contactLines,
        contactFacebookUrl,
        contactTiktokUrl,
        contactYoutubeUrl,
        footerBannerBadge,
        footerBannerTitle,
        footerBannerSubtitle,
        footerBannerPlaceholder,
        footerBannerButton,
        footerBannerSuccess,
        footerBannerImage,
        footerMainMenu,
        footerHelpMenu,
        footerCopyrightName,
        productsHeaderBadge,
        productsHeaderTitleMain,
        productsHeaderTitleNumber,
        productsHeaderTitleSuffix,
        productsHeaderDescription,
        productsHeaderFeatures,
        initializeSettings
    }
})
